/**
 * Ambient bed — looping crickets + soft wind.
 *
 * Fully procedural: no asset files. Two layers run in parallel:
 *
 *  1. Cricket pulses. Random intermittent triangle "chirps" — actually
 *     short, high-frequency bursts (~3-4kHz triangle wave with very brief
 *     envelope) panned softly across the field. Per AESTHETIC.md §7.2:
 *     sparse, varying, not a continuous wash.
 *
 *  2. Pink-noise wind. A looped pink-noise buffer through a low-pass
 *     filter, at very low gain. Suggests "distant wind through leaves"
 *     without ever foregrounding itself.
 *
 * Both layers connect to engine.ambientBus(), which already sits at 0.15
 * of the master, so the bed is well below cue-level by design.
 *
 * Lifecycle: `startAmbient()` is idempotent; `stopAmbient()` tears down
 * the nodes and clears the timeout queue. The engine being muted simply
 * silences the master — we don't pause the schedulers, so unmuting
 * resumes seamlessly.
 */

import { engine } from './engine';
import { makePinkNoiseBuffer } from './synth';

interface AmbientState {
  windSource: AudioBufferSourceNode;
  windFilter: BiquadFilterNode;
  windGain: GainNode;
  cricketGain: GainNode;
  cricketTimeout: ReturnType<typeof setTimeout> | null;
  stopped: boolean;
}

let state: AmbientState | null = null;

const EPS = 0.0001;

// Cricket chirp config — a "chirp" is 2-4 quick ticks.
const CRICKET_MIN_INTERVAL_MS = 2200;
const CRICKET_MAX_INTERVAL_MS = 6400;

export function startAmbient(): void {
  if (state) return;
  const ctx = engine.context();
  const bus = engine.ambientBus();
  if (!ctx || !bus) return;

  // --- Wind layer ---
  const windBuf = makePinkNoiseBuffer(ctx, 6); // 6s loop is long enough that the loop point isn't perceptible
  const windSource = ctx.createBufferSource();
  windSource.buffer = windBuf;
  windSource.loop = true;

  const windFilter = ctx.createBiquadFilter();
  windFilter.type = 'lowpass';
  windFilter.frequency.value = 480; // muffled, "distant"
  windFilter.Q.value = 0.4;

  const windGain = ctx.createGain();
  // Fade in over 1.5s so the bed doesn't snap on.
  const now = ctx.currentTime;
  windGain.gain.setValueAtTime(EPS, now);
  windGain.gain.linearRampToValueAtTime(0.45, now + 1.5);

  windSource.connect(windFilter).connect(windGain).connect(bus);
  windSource.start(now);

  // --- Cricket layer ---
  const cricketGain = ctx.createGain();
  cricketGain.gain.value = 0.6; // sub-bus for crickets, mixed under the ambient bus
  cricketGain.connect(bus);

  state = {
    windSource,
    windFilter,
    windGain,
    cricketGain,
    cricketTimeout: null,
    stopped: false,
  };

  scheduleNextCricket();
}

export function stopAmbient(): void {
  if (!state) return;
  const ctx = engine.context();
  const s = state;
  s.stopped = true;

  if (s.cricketTimeout !== null) {
    clearTimeout(s.cricketTimeout);
    s.cricketTimeout = null;
  }

  if (ctx) {
    const now = ctx.currentTime;
    try {
      s.windGain.gain.cancelScheduledValues(now);
      s.windGain.gain.setTargetAtTime(0, now, 0.4);
    } catch {
      s.windGain.gain.value = 0;
    }
    // Stop the wind buffer source half a second later, after the fade.
    try {
      s.windSource.stop(now + 0.8);
    } catch {
      // Already stopped.
    }
  }

  // Disconnect happens lazily via onended; in case it doesn't fire:
  setTimeout(() => {
    try {
      s.windSource.disconnect();
      s.windFilter.disconnect();
      s.windGain.disconnect();
      s.cricketGain.disconnect();
    } catch {
      // Already disconnected.
    }
  }, 1200);

  state = null;
}

function scheduleNextCricket(): void {
  const s = state;
  if (!s || s.stopped) return;
  const interval =
    CRICKET_MIN_INTERVAL_MS +
    Math.random() * (CRICKET_MAX_INTERVAL_MS - CRICKET_MIN_INTERVAL_MS);

  s.cricketTimeout = setTimeout(() => {
    playCricketChirp();
    scheduleNextCricket();
  }, interval);
}

/**
 * One cricket chirp = a short series of 2-4 quick triangle-wave ticks.
 * Frequency varies per chirp to suggest different individuals.
 */
function playCricketChirp(): void {
  const ctx = engine.context();
  const s = state;
  if (!ctx || !s || s.stopped) return;

  const baseFreq = 3200 + Math.random() * 1400; // 3200..4600 Hz
  const tickCount = 2 + Math.floor(Math.random() * 3); // 2..4 ticks
  const tickGap = 0.045 + Math.random() * 0.025; // ~45-70ms between ticks
  const startAt = ctx.currentTime + 0.02;

  // High-pass filter strips the low end so even at very low gain it
  // doesn't muddy the wind layer.
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1800;
  hp.Q.value = 0.7;
  hp.connect(s.cricketGain);

  for (let i = 0; i < tickCount; i++) {
    const at = startAt + i * tickGap;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = baseFreq * (1 + (Math.random() - 0.5) * 0.04);

    const g = ctx.createGain();
    // Very short, very quiet — a single cricket isn't loud.
    g.gain.setValueAtTime(EPS, at);
    g.gain.exponentialRampToValueAtTime(0.08, at + 0.005);
    g.gain.exponentialRampToValueAtTime(EPS, at + 0.04);
    g.gain.linearRampToValueAtTime(0, at + 0.045);

    osc.connect(g).connect(hp);
    osc.start(at);
    osc.stop(at + 0.06);

    osc.onended = () => {
      try {
        osc.disconnect();
        g.disconnect();
      } catch {
        // Already disconnected.
      }
    };
  }

  // Disconnect the per-chirp high-pass shortly after the last tick.
  setTimeout(() => {
    try {
      hp.disconnect();
    } catch {
      // Already disconnected.
    }
  }, (tickCount * tickGap + 0.2) * 1000);
}
